//! Backoffice: notizie ingerite dal feed + stato delle sorgenti.
//!
//! La lista news è la stessa che vede il front-end pubblico (`GET /v1/news`):
//! serve agli operator per capire *cosa* ha visto l'ingestion prima di
//! generare i market draft. Il pannello in alto mostra l'ultimo sync per
//! sorgente, così un feed rotto si nota subito.

import { useCallback, useEffect, useState } from "react";

import { authFetch } from "@/api/client";
import { Pill } from "@/components/Pill";

/* ──────────────────────────── tipi API ──────────────────────────── */

interface NewsItemView {
  id: string;
  source: string;
  title: string;
  summary: string | null;
  url: string;
  image_url: string | null;
  category: string | null;
  published_at: string;
}

interface FeedStatusView {
  source: string;
  last_synced_at: string | null;
  items_fetched: number;
  last_error: string | null;
}

const CATEGORIES = [
  "politics",
  "crypto",
  "sports",
  "tech",
  "science",
  "culture",
  "macro",
  "other",
];

/* ──────────────────────────── helper ──────────────────────────── */

async function extractError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { detail?: string; message?: string };
    return body.detail ?? body.message ?? `Errore (${res.status})`;
  } catch {
    return `Errore (${res.status})`;
  }
}

/** "adesso" / "12m fa" / "3h fa" sotto le 24h, data it-IT oltre. */
function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "mai";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) {
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "adesso";
  if (minutes < 60) return `${minutes}m fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h fa`;
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/* ──────────────────────────── pagina ──────────────────────────── */

export function NewsPage() {
  const [items, setItems] = useState<NewsItemView[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [category, setCategory] = useState("");

  const [feedStatus, setFeedStatus] = useState<FeedStatusView[]>([]);
  const [statusErr, setStatusErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (category) params.set("category", category);
      const res = await authFetch(`/v1/news?${params}`);
      if (!res.ok) throw new Error(await extractError(res));
      const data: unknown = await res.json();
      // Il backend restituisce un array nudo; tolleriamo comunque un wrapper
      // `{ items: [...] }` per non rompersi se la shape cambia.
      const list = Array.isArray(data)
        ? (data as NewsItemView[])
        : Array.isArray((data as { items?: unknown }).items)
          ? ((data as { items: NewsItemView[] }).items)
          : [];
      setItems(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "errore sconosciuto");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [category]);

  const loadStatus = useCallback(async () => {
    setStatusErr(null);
    try {
      const res = await authFetch("/v1/admin/feed/status");
      if (!res.ok) throw new Error(await extractError(res));
      const data: unknown = await res.json();
      setFeedStatus(Array.isArray(data) ? (data as FeedStatusView[]) : []);
    } catch (e) {
      setStatusErr(e instanceof Error ? e.message : "errore sconosciuto");
      setFeedStatus([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  return (
    <div className="flex flex-col gap-4 max-w-[1000px]">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] font-semibold">News</h1>
          <p className="text-[13px] text-muted mt-1">
            Notizie ingerite dalle sorgenti del feed. Sono il contesto da cui
            nascono i market draft.
          </p>
        </div>
        <label className="flex items-center gap-1.5">
          <span className="text-[11.5px] text-muted">Categoria</span>
          <select
            className="input w-auto py-1 text-[12px]"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">Tutte</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
      </header>

      <FeedStatusPanel status={feedStatus} error={statusErr} />

      {err && (
        <div className="card text-[12.5px] text-no">
          Caricamento notizie fallito: {err}
        </div>
      )}

      {loading ? (
        <div className="card text-muted text-[13px]">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="card text-center py-10">
          <div className="text-[14px] text-text-2">Nessuna notizia.</div>
          <div className="text-[12.5px] text-muted mt-1">
            Lancia una sincronizzazione dal feed.
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────── sub-components ───────────────────────── */

/** Pannello compatto con lo stato di ogni sorgente del feed. */
function FeedStatusPanel({
  status,
  error,
}: {
  status: FeedStatusView[];
  error: string | null;
}) {
  return (
    <section className="card flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[13px] font-semibold">Stato sorgenti</h2>
        {error && (
          <span className="text-[11.5px] text-no" title={error}>
            stato non disponibile
          </span>
        )}
      </div>

      {status.length === 0 ? (
        <div className="text-[12px] text-muted">
          {error
            ? "Impossibile leggere lo stato del feed."
            : "Nessuna sorgente configurata."}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {status.map((s) => (
            <div
              key={s.source}
              className="flex items-center gap-2 rounded-md border border-line bg-bg-2 px-3 py-1.5"
              title={s.last_error ?? undefined}
            >
              <Pill tone={s.last_error ? "no" : "yes"}>
                {s.last_error ? "KO" : "OK"}
              </Pill>
              <div className="leading-tight">
                <div className="text-[12.5px] text-text">{s.source}</div>
                <div className="text-[11px] text-muted font-mono">
                  {formatRelative(s.last_synced_at)} · {s.items_fetched} item
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** Card singola notizia. La thumbnail si nasconde se l'URL è assente o rotto. */
function NewsCard({ item }: { item: NewsItemView }) {
  const [imageOk, setImageOk] = useState(true);
  const showImage = Boolean(item.image_url) && imageOk;

  return (
    <article className="card flex gap-3 items-start">
      {showImage && item.image_url && (
        <img
          src={item.image_url}
          alt=""
          loading="lazy"
          className="h-[72px] w-[108px] shrink-0 rounded-md object-cover bg-bg-3"
          onError={() => setImageOk(false)}
        />
      )}
      <div className="min-w-0 flex-1 flex flex-col gap-1">
        <a
          className="text-[14px] font-medium text-text hover:text-accent-2 transition-colors"
          href={item.url}
          target="_blank"
          rel="noreferrer"
        >
          {item.title}
        </a>
        <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-muted">
          <span className="text-text-2">{item.source}</span>
          <span>·</span>
          <span className="font-mono">{formatRelative(item.published_at)}</span>
          {item.category && <Pill tone="accent">{item.category}</Pill>}
        </div>
        {item.summary && (
          <p className="text-[12.5px] text-text-2 leading-relaxed mt-0.5">
            {item.summary}
          </p>
        )}
      </div>
    </article>
  );
}
